import React, { useEffect, useState, useContext } from 'react';
import { WebSocketContext } from '../WebSocketContext';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { Refresh, TrendingUp, TrendingDown, AccountBalance, Description, Close, Search } from '@mui/icons-material';
import BottomSheetClosePosition from '../components/BottomSheetClosePosition';
import ScriptIcon from '../components/ScriptIcon';
import { mediatorAppFetch, checkAppSession } from '../utils/apiFetch';
import './../NetPositions.css';

const Positions = () => {
  const { userDetails, stocks, netPositions, summary, refreshTradesAndPortfolio } = useContext(WebSocketContext);
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isClosingAll, setIsClosingAll] = useState(false);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Handle navigation state from StockCard
  useEffect(() => {
    if (location.state?.filterByScript) {
      setSearchQuery(location.state.filterByScript);
    }
  }, [location.state]);

  const handleRefresh = () => {
    refreshTradesAndPortfolio("both");
  };


  const handleTradesClick = (e, position) => {
    e.stopPropagation(); // Prevent card click
    // Navigate to trades page with specific script filter
    navigate('/trades', { 
      state: { 
        filterByScript: position.stockName,
        instrumentToken: position.instrument_token 
      } 
    });
  };

  const resolvePositionLotSize = (pos) => {
    const size = Number(pos?.usedLotSize || pos?.stockLotSize || pos?.lotSize || 1);
    return size > 0 ? size : 1;
  };

  const resolvePositionLotVariant = (pos) => {
    const variant = String(pos?.lotVariant || 'MEGA').toUpperCase();
    return variant === 'MINI' ? 'MINI' : 'MEGA';
  };

  const handlePositionClick = async (position) => {
    const sessionOk = await checkAppSession();
    if (!sessionOk) {
      return;
    }

    // Format position for BottomSheetClosePosition
    // BottomSheetClosePosition expects buyQty and sellQty separately
    const effectiveLotSize = resolvePositionLotSize(position);
    const lotVariant = resolvePositionLotVariant(position);
    const formattedPosition = {
      ...position,
      // Ensure buyQty and sellQty are set correctly based on netQty
      buyQty: position.buyQty > 0 ? Math.abs(position.netQty) : 0,
      sellQty: position.buyQty === 0 && position.netQty !== 0 ? Math.abs(position.netQty) : 0,
      lotVariant,
      usedLotSize: effectiveLotSize,
      stockLotSize: effectiveLotSize,
      lotSize: effectiveLotSize,
    };
    setSelectedPosition(formattedPosition);
    setIsBottomSheetOpen(true);
  };

  const handleCloseBottomSheet = () => {
    setIsBottomSheetOpen(false);
    setSelectedPosition(null);
  };

  const buildMediatorClosePayload = (position, stock) => {
    const orderType = position.buyQty > 0 ? 2 : 1;
    const orderQuantity = Math.abs(position.netQty);
    const lotSize = resolvePositionLotSize(position);
    const lotVariant = resolvePositionLotVariant(position);
    const orderLots = orderQuantity / lotSize;

    const positionData = {
      ...position,
      buyQty: position.buyQty > 0 ? orderQuantity : 0,
      sellQty: position.buyQty === 0 && position.netQty !== 0 ? orderQuantity : 0,
      lotVariant,
      usedLotSize: lotSize,
      stockLotSize: lotSize,
      lotSize,
      dispName: position.dispname || position.stockName,
      stock: {
        instrumentIdentifier: position.stockId,
        exchange: position.exchange,
        dispName: position.stockName || position.dispname,
        ...stock,
        lotVariant,
        usedLotSize: lotSize,
        stockLotSize: lotSize,
        lotSize,
      },
    };

    return {
      stock: positionData,
      orderTypeBuySell: orderType,
      orderExecutionType: 1,
      lots: Number(orderLots),
      quantity: Math.floor(orderQuantity),
      entryPrice: orderType === 1 ? positionData.avgSellPrice : positionData.avgBuyPrice,
      orderTransactionType: 2,
      orderDevice: 1,
      limitSlPrice: 0,
      lotVariant,
      ip: process.env.REACT_APP_USER_IP,
    };
  };

  // Close All functionality
  const handleCloseAll = async () => {
    const openPositions = filteredPositions.filter(pos => pos.netQty != 0);
    
    if (openPositions.length === 0) {
      alert('No open positions to close.');
      return;
    }
    
    // Show detailed confirmation
    const totalValue = openPositions.reduce((sum, pos) => {
      const stock = stocks[pos.instrument_token] || {};
      return sum + (Math.abs(pos.netQty) * (pos.buyQty > 0 ? stock.Ask : stock.Bid));
    }, 0);
    
    const confirmMessage = `
      Close All Positions:
      • ${openPositions.length} positions
      • Total Value: ${totalValue.toLocaleString('en-IN', {minimumFractionDigits: 2})}
      
      This will create market orders to close all positions.
      Are you sure?
    `;
    
    if (!window.confirm(confirmMessage)) return;
    
    try {
      setIsClosingAll(true);
      
      const closeResults = await Promise.all(
        openPositions.map(async (position) => {
          const stock = stocks[position.instrument_token] || {};
          try {
            const response = await mediatorAppFetch({
              method: 'POST',
              body: JSON.stringify(buildMediatorClosePayload(position, stock)),
            });
            const data = await response.json();
            return { success: data.success === true, message: data.message };
          } catch (err) {
            return { success: false, message: err?.message || 'Request failed' };
          }
        })
      );

      const successful = closeResults.filter((r) => r.success).length;
      const failed = closeResults.length - successful;
      
      if (failed > 0) {
        alert(`Close All completed: ${successful} successful, ${failed} failed`);
      } else {
        alert(`All ${successful} positions closed successfully!`);
      }
      
      // Refresh data
      refreshTradesAndPortfolio("both");
      
    } catch (error) {
      console.error('Close All Error:', error);
      alert('Error closing positions. Please try again.');
    } finally {
      setIsClosingAll(false);
    }
  };

  // Filter positions based on search query
  const filteredPositions = (Array.isArray(netPositions) ? netPositions : []).filter(position => {
    if (!searchQuery) return true;
    
    // Check if searchQuery is from StockCard navigation (exact match)
    if (location.state?.filterByScript) {
      return position.stockName?.toLowerCase() === searchQuery.toLowerCase() ||
             position.dispname?.toLowerCase() === searchQuery.toLowerCase() ||
             position.stockId == searchQuery;
    }
    
    // Regular search (partial match for manual typing)
    return position.stockName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
           position.dispname?.toLowerCase().includes(searchQuery.toLowerCase()) ||
           position.stockId == searchQuery;
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  };

  const getPnLColor = (pnl) => {
    if (pnl > 0) return 'positive';
    if (pnl < 0) return 'negative';
    return 'neutral';
  };

  const getPositionSideColor = (side) => {
    switch (side) {
      case 'BUY': return 'buy';
      case 'SELL': return 'sell';
      default: return 'neutral';
    }
  };

  // Calculate PnL function (using live prices from stocksLT)
  const calculatePnL = (position, stock) => {
    if (position.buyQty > 0) {
      
      // Use live bid price from stocksLT, fallback to stock context, then position data
      const currentBid = stock.Bid || position.buyPrice || 0;
      if (currentBid === 0) { 
        return 0;
      } else { 
        // console.log(currentAsk, Number(position.avgBuyPrice), Number(position.netQty));
        return (currentBid * Number(position.netQty)) - (Number(position.avgBuyPrice) * Number(position.netQty));
      }
    } else {
      // Use live ask price from stocksLT, fallback to stock context, then position data
      const currentAsk = stock.Ask || position.sellPrice || 0;
      if (currentAsk === 0) { 
        return 0;
      } else {
        const netQty = Number(position.netQty);
        const qtyAbs = Math.abs(netQty); // Short positions: netQty is negative, so use abs quantity for P&L.
        const pnl = (Number(position.avgSellPrice) - currentAsk) * qtyAbs;
        // console.log('PnL SELL', {
        //   instrument_token: position.instrument_token,
        //   avgSellPrice: Number(position.avgSellPrice),
        //   currentAsk,
        //   netQty,
        //   qtyAbs,
        //   pnl,
        // });
        return pnl;
      }
    }
  };

  const currentPnL = (netPositions || [])
    .filter((pos) => pos.netQty != 0)
    .reduce((total, pos) => {
      const stock = stocks[pos.instrument_token] || {};
      return total + calculatePnL(pos, stock);
    }, 0);
  const bookedPnL = Number(summary.totalRealizedPnL) || 0;
  const netPnL = currentPnL + bookedPnL;

  if (loading) {
    return (
      <div className={`net-positions-page ${isDarkMode ? 'dark-theme' : 'light-theme'}`}>
        <div className="net-pos-loading-container">
          <div className="net-pos-loading-spinner"></div>
          <p>Loading net positions...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`net-positions-page ${isDarkMode ? 'dark-theme' : 'light-theme'}`}>
        <div className="net-pos-error-container">
          <div className="net-pos-error-icon">⚠️</div>
          <h3>Error Loading Positions</h3>
          <p>{error}</p>
          <button className="net-pos-retry-btn" onClick={handleRefresh}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`net-positions-page ${isDarkMode ? 'dark-theme' : 'light-theme'}`} data-theme={isDarkMode ? 'dark' : 'light'}>
      <button className="refresh-button" onClick={handleRefresh}><Refresh /></button>
      {/* Compact Theme Header Section */}
      <div className="net-pos-header-section">
        <div className="net-pos-header-content">
          <div className="pnl-cards-container">
            <div className="pnl-card">
              <div className="pnl-card-header">
                <span className="pnl-card-title">Current P&L</span>
              </div>
              <div className="pnl-card-value">
                <span className={`pnl-amount ${getPnLColor(currentPnL)}`}>
                  {currentPnL.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>
            <div className="pnl-card">
              <div className="pnl-card-header">
                <span className="pnl-card-title">Booked P&L</span>
              </div>
              <div className="pnl-card-value">
                <span className={`pnl-amount ${getPnLColor(bookedPnL)}`}>
                  {bookedPnL.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>
            <div className="pnl-card">
              <div className="pnl-card-header">
                <span className="pnl-card-title">Net P&L</span>
              </div>
              <div className="pnl-card-value">
                <span className={`pnl-amount ${getPnLColor(netPnL)}`}>
                  {netPnL.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* White Content Section */}
      <div className="net-pos-content-section">
        {/* Positions Header */}
        {/* <div className="net-pos-positions-header">
          <h2 className="net-pos-positions-title">Positions</h2>
          <div className="net-pos-positions-controls">
            <div className="net-pos-control-icons">
              <span className="net-pos-filter-icon">⋮</span>
              <span className="net-pos-sort-icon">⇅</span>
            </div>
            <button 
              className="net-pos-close-all-btn" 
              onClick={handleCloseAll}
              disabled={isClosingAll || filteredPositions.filter(pos => pos.netQty != 0).length === 0}
            >
              {isClosingAll ? 'Closing...' : 'Close All'}
            </button>
          </div>
        </div> */}

        {/* Search Bar */}
        <div className="search-container-netpositions">
          <Search style={{ height: '20px', color: 'var(--text-muted)' }} />
          <input
            type="text"
            id="search-netpositions-input"
            name="search-netpositions"
            placeholder="Search Script"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input-netpositions"
          />
        </div>

        {/* Positions List */}
        <div className="net-pos-positions-list">
          {filteredPositions.length === 0 ? (
            <div className="net-pos-empty-state">
              {/* <div className="net-pos-empty-icon">
                <AccountBalance />
              </div> */}
              <h3>No Net Positions</h3>
              <p>You don't have any net positions at the moment.</p>
              {/* <button className="net-pos-primary-action-btn" onClick={() => navigate('/')}>
                Start Trading
              </button> */}
            </div>
          ) : (
            filteredPositions.map((position) => {
              const stock = stocks[position.instrument_token] || {};
              const calculatedPnL = calculatePnL(position, stock);
              
              // Format expiry date
              const formatExpiry = (expiry) => {
                if (!expiry) return '';
                try {
                  const date = new Date(expiry);
                  const day = date.getDate();
                  const month = date.toLocaleString('en-US', { month: 'short' });
                  const year = date.getFullYear();
                  return `${day}-${month}-${year}`;
                } catch {
                  return expiry;
                }
              };
              
              const quantityColor = position.buyQty > 0 ? 'green' : 'red';
              const pnlColor = position.netQty != 0 
                ? (calculatedPnL > 0 ? 'green' : calculatedPnL < 0 ? 'red' : '')
                : (position.realizedPnL > 0 ? 'green' : position.realizedPnL < 0 ? 'red' : '');
              
              return ( 
                <div 
                  key={position.positionId} 
                  className="net-pos-position-item"
                  onClick={() => position.netQty != 0 && handlePositionClick(position)}
                  style={{ cursor: position.netQty != 0 ? 'pointer' : 'default' }}
                >
                  {/* Row 1: Asset Name + Date (left) | P&L (right) */}
                  <div className="net-pos-row net-pos-row-top">
                    <div className="net-pos-row-left">
                      <h4 className="net-pos-asset-name">
                        <ScriptIcon name={position.dispname || position.stockName} symbol={position.scriptName} size={28} />
                        <span className="script-identity-text">
                          <span>
                            {String(position.dispname || position.stockName || '').replace(/ MINI$/i, '')}
                            {resolvePositionLotVariant(position) === 'MINI' && (
                              <span className="lot-variant-mini">MINI</span>
                            )}
                          </span>
                          <span className="net-pos-expiry-date">
                            {position.expiry ? formatExpiry(position.autoCutDate) : ''}
                          </span>
                        </span>
                      </h4>
                    </div>
                    <div className="net-pos-row-right">
                      {position.netQty != 0 && (
                        <>
                          <p className={`net-pos-pnl-value ${pnlColor}`}>
                            {calculatedPnL.toLocaleString('en-IN', {minimumFractionDigits: 2})}
                          </p>
                          <p className="net-pos-price-range">
                            {position.buyQty > 0
                              ? `${Number(position.avgBuyPrice || 0).toFixed(2)} >> ${Number(stock.Bid || position.buyPrice || 0).toFixed(2)}`
                              : `${Number(position.avgSellPrice || 0).toFixed(2)} >> ${Number(stock.Ask || position.sellPrice || 0).toFixed(2)}`
                            }
                          </p>
                        </>
                      )}
                      {position.netQty == 0 && (
                        <p className={`net-pos-pnl-value ${pnlColor}`}>
                          {Number(position.realizedPnL || 0).toLocaleString('en-IN', {minimumFractionDigits: 2})}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {/* Row 2: Qty, side, avg, live price */}
                  {position.netQty != 0 && (
                    <div className="net-pos-stats">
                      <div className="net-pos-stat">
                        <span className="net-pos-stat-label">Qty</span>
                        <span className={`net-pos-stat-value ${quantityColor}`}>
                          {position.buyQty > 0 ? '+' : '-'}{Math.abs(Number(position.netQty)).toFixed(2)}
                        </span>
                      </div>
                      <div className="net-pos-stat">
                        <span className="net-pos-stat-label">Side</span>
                        <span className={`net-pos-stat-value ${quantityColor}`}>
                          {position.buyQty > 0 ? 'Buy' : 'Sell'}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Close Position Bottom Sheet */}
      {selectedPosition && (
        <BottomSheetClosePosition
          userDetails={userDetails}
          position={selectedPosition}
          stock={stocks[selectedPosition.instrument_token] || {}}
          isOpen={isBottomSheetOpen}
          onClose={handleCloseBottomSheet}
        />
      )}
    </div>
  );
};

export default Positions;

